import type { ReactNode } from "react";
import { Outlet } from "react-router";
import { StudentSidebar } from "@/features/student/components/student-sidebar";
import { StudentMobileNav } from "@/features/student/components/student-mobile-nav";
import { TutorLauncher } from "@/features/tutor/tutor-launcher";

/**
 * Applies only to /dashboard and /exams (the list) — deliberately NOT to
 * /exams/[id]/take (its own full-width timer/navigator interface would
 * clash with a second sidebar, and its own bottom action bar would
 * collide with a fixed bottom tab bar) or /attempts/[id]/result (a
 * focused, single-column results view). Those two routes live as direct
 * siblings under (student)/, outside this group, so they keep just the
 * SiteHeader from the outer layout with no additional chrome.
 *
 * `pb-16 lg:pb-0` on the content wrapper reserves space for
 * StudentMobileNav's fixed bottom bar on small screens (where it's
 * visible) without affecting desktop layout (where it's hidden and the
 * sidebar is used instead).
 *
 * Access control happens one level up (StudentLayout's RequireAuth), so
 * no page in this group ever renders for a signed-out visitor.
 */
export function WithSidebarLayout({ children }: { children?: ReactNode }) {
  return (
    <div className="flex min-h-[calc(100vh-4rem)] flex-1">
      <StudentSidebar />
      {/*
        min-w-0 here matches the same fix in admin-layout.tsx: this is a
        `flex-1` item in a sidebar+content row, and flex items default to
        `min-width: auto` — refusing to shrink below their content's
        natural width. Without it, one long unwrapped string anywhere in
        the page (a leaderboard name, a stat row, etc.) can stretch this
        whole column past "viewport minus sidebar" and force a page-wide
        horizontal scrollbar, even when that string's own element has its
        own `truncate`/`min-w-0` — that inner fix only works once this
        outer column is actually capped to the space it's meant to have.
      */}
      <div className="min-w-0 flex-1 bg-muted/30 pb-36 lg:pb-24">{children ?? <Outlet />}</div>
      <StudentMobileNav />
      <TutorLauncher />
    </div>
  );
}
